import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Box, Button, Card, Stack } from "@common/components/";
import { Typography } from "@curves/ui-base";
import { TabsCardLayout } from "@curves/ui-shared";
import useTabState from "@util/hook/useTabState";
import useClubInventoryApi from "@apis/useClubInventoryApi";
import useOptions from "@util/hook/useOptions";
import { isFunction } from "@util/utils";
import { CLUB_INVENTORY_ROUTER } from "@clubInventory/router/routerPath";
import { INVENTORY_RECORD_TYPE } from "@clubInventory/constants/dataEnums";
import StockSummaryCard from "./components/StockSummaryCard";
import StockChangeHistory from "./components/StockChangeHistory";
import StockDefectRecords from "./components/StockDefectRecords";
import StockDialogBox from "./dialog/StockDialogBox";
import { ShipmentDialogBox } from "@clubInventory/shipment/dialog";

// 功能：頁籤定義
const TAB_VALUES = {
	CHANGE_HISTORY: "change-history",
	DEFECT_RECORDS: "defect-records",
};
const TABS = [
	{ label: "異動紀錄", value: TAB_VALUES.CHANGE_HISTORY },
	{ label: "瑕疵紀錄", value: TAB_VALUES.DEFECT_RECORDS },
];
const TABS_CONTENT_PROPS = { className: "px-3 pb-3" };

// 功能：API detail → StockSummaryCard props 格式
const normalizeDetail = detail => {
	if (!detail) return null;
	return {
		product: {
			sku: detail.sku ?? "",
			name: detail.productName ?? "",
			spec: detail.variantName ?? "",
			category: detail.categoryName ?? "",
		},
		lastInventoryDate: detail.lastInventoryCountDate ?? null,
		defaultPrice: detail.defaultSalePrice ?? null,
		sellableQty: detail.quantity ?? null,
		hasQuantityAdjustmentOrder: detail.hasQuantityAdjustmentOrder === true || detail.isAbnormal === true,
	};
};

// 功能：API 異動統計 → StockChangeHistory summary 格式
const normalizeChangeSummary = summary => {
	if (!summary) return { originalTotal: 0, inbound: 0, outbound: 0, totalInventory: 0, lastInventoryCountDate: null };
	return {
		originalTotal: summary.originalQuantity ?? 0,
		inbound: summary.stockInQuantity ?? 0,
		outbound: summary.stockOutQuantity ?? 0,
		totalInventory: summary.totalQuantity ?? 0,
		lastInventoryCountDate: summary.lastInventoryCountDate ?? null,
	};
};

// 功能：商品庫存明細頁（摘要卡片、異動紀錄 Tab、瑕疵紀錄 Tab）
const StockDetailPage = () => {
	const params = useParams();
	const productID = Number(params.productID) || 0;
	const variantID = Number(params.variantID) || 0;

	const dialogBoxRef = useRef(null);
	const shipmentDialogBoxRef = useRef(null);
	const { activeTab: tabValue, handleTabChange } = useTabState(TAB_VALUES.CHANGE_HISTORY);

	const { getClubInventoryOptionsApi, postClubInventoryRecordsApi, postClubInventoryDefectiveRecordsApi } = useClubInventoryApi();
	const { options } = useOptions({ getOptionsApi: getClubInventoryOptionsApi, isFetchOnMount: true });

	// 功能：商品摘要（來自 postClubInventoryRecordsApi detail，mount 時取得，操作後刷新）
	const [detail, setDetail] = useState(null);

	// 功能：異動紀錄 Tab 資料（list + pagination）與摘要
	// 異動區間：0 全部、1 盤點後、2 盤點前（對應 ClubInventoryRecordRangeEnum）
	const [changeHistoryOverviewData, setChangeHistoryOverviewData] = useState({ list: [], pagination: null });
	const [changeSummary, setChangeSummary] = useState(null);
	const [recordRange, setRecordRange] = useState(1);
	const [recordType, setRecordType] = useState(INVENTORY_RECORD_TYPE.ALL);

	// 功能：瑕疵紀錄 Tab 資料（list + pagination），切換至瑕疵 Tab 時 lazy 載入
	const [defectOverviewData, setDefectOverviewData] = useState({ list: [], pagination: null });

	// 功能：載入商品摘要（detail）與異動紀錄，recordRange 或換頁時重新載入
	const fetchChangeHistory = useCallback(
		async (pageIndex = 1) => {
			if (!productID) {
				setDetail(null);
				setChangeHistoryOverviewData({ list: [], pagination: null });
				setChangeSummary(null);
				return;
			}
			try {
				const pageSize = 50;
				const resp = await postClubInventoryRecordsApi({ productID, variantID, pageIndex, pageSize, recordRange, recordType });
				const { list = [], totalCount: total = 0, totalPage = 0 } = resp?.records ?? {};
				setDetail(normalizeDetail(resp?.detail ?? null));
				setChangeHistoryOverviewData({
					list,
					pagination: {
						pageIndex,
						totalPage,
						totalCount: total,
					},
				});
				setChangeSummary(normalizeChangeSummary(resp?.summary ?? null));
			} catch {
				setDetail(null);
				setChangeHistoryOverviewData({ list: [], pagination: null });
				setChangeSummary(null);
			}
		},
		[productID, variantID, recordRange, recordType, postClubInventoryRecordsApi],
	);

	const updateChangeHistoryPage = useCallback(page => fetchChangeHistory(page), [fetchChangeHistory]);

	useEffect(() => {
		fetchChangeHistory();
	}, [fetchChangeHistory]);

	// 功能：載入瑕疵紀錄（支援換頁），僅在瑕疵 Tab 觸發
	const fetchDefectRecords = useCallback(
		async (pageIndex = 1) => {
			if (!productID) return;
			try {
				const resp = await postClubInventoryDefectiveRecordsApi({ productID, variantID, pageIndex, pageSize: 10 });
				const { list = [], totalCount: total = 0, totalPage = 0 } = resp?.records ?? {};
				setDefectOverviewData({
					list,
					pagination: {
						pageIndex,
						totalPage,
						totalCount: total,
					},
				});
			} catch {
				setDefectOverviewData({ list: [], pagination: null });
			}
		},
		[productID, variantID, postClubInventoryDefectiveRecordsApi],
	);

	const updateDefectPage = useCallback(page => fetchDefectRecords(page), [fetchDefectRecords]);

	useEffect(() => {
		if (tabValue === TAB_VALUES.DEFECT_RECORDS) fetchDefectRecords();
	}, [tabValue, fetchDefectRecords]);

	// 功能：開啟可販售數量調整彈窗，送出後刷新 detail（含最新 sellableQty）
	const handleEditSellableQty = useCallback(() => {
		const { openAdjustStockQtyDialog } = dialogBoxRef.current ?? {};
		if (isFunction(openAdjustStockQtyDialog)) openAdjustStockQtyDialog({ productID, variantID }, fetchChangeHistory);
	}, [fetchChangeHistory, productID, variantID]);

	// 功能：開啟瑕疵回報彈窗，送出後同步刷新瑕疵列表與 detail（回報影響 sellableQty）
	// 若勾選「同步執行退貨流程」且回報成功，由 onSyncReturnFlow 開啟退貨單彈窗
	const handleOpenDefectReport = useCallback(() => {
		const { openConvertToDefectDialog } = dialogBoxRef.current ?? {};
		if (isFunction(openConvertToDefectDialog)) {
			const onRefresh = () => {
				fetchDefectRecords();
				fetchChangeHistory();
			};
			openConvertToDefectDialog({ productID, variantID }, onRefresh, () => shipmentDialogBoxRef.current?.openDefectReturnDialog(null, onRefresh));
		}
	}, [fetchDefectRecords, fetchChangeHistory, productID, variantID]);

	// 功能：依當前頁籤決定 Tab 右側操作按鈕
	const actions = useMemo(() => {
		if (tabValue === TAB_VALUES.DEFECT_RECORDS) {
			return [{ label: "瑕疵回報", variant: "outlined", color: "error", onClick: handleOpenDefectReport }];
		}
		return [];
	}, [tabValue, handleOpenDefectReport]);

	return (
		<Box className="px-3 py-2 stock-detail-page">
			<Stack justifyContent="space-between" alignItems="center" className="mb-2">
				<Typography variant="h5" className="mb-0 font-weight-bold">
					商品庫存明細
				</Typography>
				<Button component={NavLink} to={CLUB_INVENTORY_ROUTER.STOCK.OVERVIEW} variant="contained">
					返回總覽
				</Button>
			</Stack>

			{/* 商品庫存摘要卡片 */}
			<Card className="p-3 mb-3">
				<StockSummaryCard
					product={detail?.product}
					lastInventoryDate={detail?.lastInventoryDate}
					defaultPrice={detail?.defaultPrice}
					sellableQty={detail?.sellableQty}
					canAdjust={detail?.hasQuantityAdjustmentOrder ?? false}
					onEditSellableQty={handleEditSellableQty}
				/>
			</Card>

			{/* 頁籤：異動紀錄、瑕疵紀錄 */}
			<TabsCardLayout className="stock-detail-tabs" tabs={TABS} activeTab={tabValue} actions={actions} onTabChange={handleTabChange} tabsContent={TABS_CONTENT_PROPS}>
				{tabValue === TAB_VALUES.CHANGE_HISTORY && (
					<StockChangeHistory
						changeHistoryOverviewData={changeHistoryOverviewData}
						updateChangeHistoryPage={updateChangeHistoryPage}
						className="mt-3"
						lastInventoryDate={changeSummary?.lastInventoryCountDate ?? detail?.lastInventoryDate ?? null}
						summary={changeSummary ?? { originalTotal: 0, inbound: 0, outbound: 0, totalInventory: 0 }}
						recordRangeOptions={options?.recordRangeOptions}
						recordRange={recordRange}
						onRecordRangeChange={setRecordRange}
						recordType={recordType}
						onRecordTypeChange={setRecordType}
					/>
				)}
				{tabValue === TAB_VALUES.DEFECT_RECORDS && <StockDefectRecords defectOverviewData={defectOverviewData} updateDefectPage={updateDefectPage} className="mt-3" />}
			</TabsCardLayout>

			<StockDialogBox ref={dialogBoxRef} />
			<ShipmentDialogBox ref={shipmentDialogBoxRef} />
		</Box>
	);
};

export default StockDetailPage;
